import { useMemo } from "react";
import { useUser } from "../../context/UserContext";
import useGetPowerMenu, { PowerMenuBlock } from "./useGetPowerMenu";
import { RouteType } from "../../routes/routes";
import LoadingMenuItem from "../../components/LoadingMenuItem";

const BLOCK_ICONS: Record<string, string> = {
    chiffres_cles: "bx bx-trending-up",
    activite: "bx bx-bar-chart-square",
    animation: "bx bx-envelope",
    base_client: "bx bx-group",
    fidelite: "bx bx-heart",
    dashboard: "bx bx-dashboard",
};

const getIconForBlock = (blockKey: string): string => {
    return BLOCK_ICONS[blockKey] || "bx bx-bar-chart";
};

const useGetDynamicReportingMenu = (): {
    reportingMenuItems: RouteType;
    loading: boolean;
    error: string;
    refetch: () => void;
} => {
    const { selectedWorkspace } = useUser();
    const workspaceName = selectedWorkspace?.volumeName || '';
    const { data: powerMenuData, loading, error, refetch } = useGetPowerMenu({ 
        shouldFetch: !!workspaceName, 
        workspace: workspaceName 
    });

    const reportingMenuItems = useMemo<RouteType>(() => {
        const baseMenu: RouteType = {
            name: "Reporting DataViz",
            icon: "bx bx-bar-chart",
            children: [],
            isLoading: false,
        };

        if (loading || !selectedWorkspace?.volumeName) {
            return {
                ...baseMenu,
                children: [{ name: "Chargement...", component: LoadingMenuItem }],
                isLoading: true,
            };
        }

        if (!powerMenuData) return baseMenu;

        const workspaceNameLower = selectedWorkspace.volumeName.toLowerCase();
        const { blocks, reportsLabels, powerBiParams, powerBiReports } = powerMenuData;
        const workspaceParams = powerBiParams[workspaceNameLower];

        if (!workspaceParams) {
            console.warn(`No Power BI params found for workspace: ${workspaceNameLower}`);
            return baseMenu;
        }

        const { active, customer_visible, version } = workspaceParams;
        if (active !== "1" || customer_visible !== "1" || version !== "2") {
            return baseMenu;
        }

        const workspaceReports = powerBiReports[workspaceNameLower];
        if (!workspaceReports) {
            console.warn(`No reports found for workspace: ${workspaceNameLower}`);
            return baseMenu;
        }

        const children = Object.entries(blocks)
            .map(([blockKey, blockInfo]) => {
                const reports = workspaceReports[blockKey];
                if (!reports?.length) return null;

                return {
                    name: (blockInfo as PowerMenuBlock).label,
                    icon: getIconForBlock(blockKey),
                    children: reports.map((reportKey) => ({
                        path: `/reporting/${blockKey}/${reportKey}`,
                        name: reportsLabels[reportKey] || reportKey,
                    })),
                };
            })
            .filter(Boolean) as RouteType[];

        return children.length > 0 ? { ...baseMenu, children } : baseMenu;
    }, [powerMenuData, selectedWorkspace, loading]);

    return { reportingMenuItems, loading, error, refetch };
};

export default useGetDynamicReportingMenu;
